import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  styled,
  Theme,
} from '@mui/material'
import upperFirst from 'lodash/upperFirst'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import urljoin from 'url-join'

import BrandLogo from 'src/components/BrandLogo'
import ErrorSnackbar from 'src/components/Snackbar'
import backgroundImage from 'src/img/background.jpg'

const Logo = styled(BrandLogo)(() => ({
  width: 300,
  maxWidth: '100%',
}))

// Chrome paints autofilled inputs with a background of its own, which stops at the input and leaves
// the adornment behind, so the field ends up two-tone. Repaint it with the field's own background.
const LoginField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
    WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
    WebkitTextFillColor: theme.palette.text.primary,
  },
}))

interface ILoginPageProps {
  errorMessage?: string
  // Held by the parent: this page unmounts while a login runs, and a failed one must not hide the form again.
  isAdministratorLogin: boolean
  onAdministratorLogin: () => void
  onErrorMessageClose?: () => void
  onValidate: (username: string, password: string) => void
  // When false, the password form is kept for administrators behind a link.
  withPasswordLogin: boolean
  withSAML?: boolean
}

const LoginPage = (props: ILoginPageProps) => {
  const {
    onValidate,
    errorMessage = '',
    onErrorMessageClose,
    withPasswordLogin,
    isAdministratorLogin,
    onAdministratorLogin,
  } = props
  const { t } = useTranslation()
  const isPasswordFormShown = withPasswordLogin || isAdministratorLogin
  const [showPassword, setPasswordDisplay] = useState<boolean>(false)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [isErrorMessageOpen, setErroMesgOpen] = useState<boolean>(false)
  const [username, setUsername] = useState<string>('')
  const [password, setPassword] = useState<string>('')

  const SAMLURLbasename = urljoin(
    process.env.REACT_APP_SERVER_HOSTNAME || window.location.origin,
    '/saml2/login/?next='
  )

  useEffect(() => {
    if (errorMessage) {
      setErroMesgOpen(true)
    }
  }, [errorMessage])

  const handleClickShowPassword = () => {
    setPasswordDisplay((passwordDisplayed) => !passwordDisplayed)
  }

  const handleUsernameChange = (evt: any) => {
    setUsername(evt.target.value)
  }

  const handlePasswordChange = (evt: any) => {
    setPassword(evt.target.value)
  }

  const handleButtonClick = () => {
    setLoading(true)
    if (onValidate) {
      onValidate(username, password)
    }
    setLoading(false)
  }

  const handleErrorMessageClose = () => {
    setErroMesgOpen(false)
    if (onErrorMessageClose) {
      onErrorMessageClose()
    }
  }

  const ssoButton = (
    <Button
      variant={isPasswordFormShown ? 'outlined' : 'contained'}
      color="primary"
      fullWidth
      disabled={isLoading}
      href={SAMLURLbasename + window.location.origin}
      sx={{ height: 40 }}
    >
      {t('continue with sso')}
    </Button>
  )

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'space-around',
        background: `url(${backgroundImage}) no-repeat center center fixed`,
      }}
    >
      <Paper
        elevation={12}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' && isPasswordFormShown) {
            handleButtonClick()
          }
        }}
        sx={{
          width: 470,
          maxWidth: '100%',
          px: 6,
          py: 10,
        }}
      >
        <Stack spacing={10} sx={{ height: '100%', justifyContent: 'space-around' }}>
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Logo />
          </Box>
          <Stack spacing={6}>
            {isPasswordFormShown ? (
              <>
                <LoginField
                  id="adornment-username"
                  label={upperFirst(t('username'))}
                  variant="outlined"
                  fullWidth
                  onChange={handleUsernameChange}
                  autoFocus={true}
                />
                <Stack spacing={2}>
                  <LoginField
                    id="adornment-password"
                    type={showPassword ? 'text' : 'password'}
                    label={upperFirst(t('password'))}
                    variant="outlined"
                    fullWidth
                    onChange={handlePasswordChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={upperFirst(t('toggle password visibility'))}
                            onClick={handleClickShowPassword}
                          >
                            {!showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {withPasswordLogin && (
                    <Box display="flex" justifyContent="flex-end" sx={{ width: '100%' }}>
                      <Link href="/password/reset" underline="none" sx={{ color: 'text.secondary' }}>
                        {t('button.forgot_password')}
                      </Link>
                    </Box>
                  )}
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isLoading}
                  onClick={handleButtonClick}
                  sx={{ height: 40 }}
                >
                  {t('button.login')}
                </Button>
                {isLoading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
                {props.withSAML && ssoButton}
              </>
            ) : (
              <>
                {props.withSAML && ssoButton}
                <Link
                  component="button"
                  underline="none"
                  onClick={onAdministratorLogin}
                  sx={{ color: 'text.secondary', alignSelf: 'center' }}
                >
                  {t('button.administratorLogin')}
                </Link>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>
      <ErrorSnackbar open={isErrorMessageOpen} onClose={handleErrorMessageClose} message={errorMessage} />
    </Box>
  )
}

export default LoginPage
